import { useViewportSize } from '@mantine/hooks';
import { ThankYouShareProps } from '../../types/PropsTypes';
import { useContext, useEffect, useState } from 'react';
import { CompanyContext } from '../../contexts/CompanyContext';
import {
  IconBrandInstagram,
  IconCopy,
  IconDeviceFloppy,
  IconMail,
  IconMessage2,
  IconShare,
} from '../icons/TablerIcons';
import { facebookIconPrimary, tiktokIcon, xIcon } from '../icons/BrandIcons';
import { Title } from '@mantine/core';
import {
  preloadFacebookSdk,
  safeOpen,
  shareToFacebook,
} from '../../services/UtilService';
import { useOfferImage } from '../../hooks/useOfferImage';
import { detectIos, detectTouchDevice, shareOptions } from '../../services/shareOptions';
import { offerImageProxyUrl } from '../../services/offerImageUrl';

async function createShareImageFile(
  imageRequestUrl: string,
  signal?: AbortSignal,
): Promise<File | null> {
  const response = await fetch(imageRequestUrl, {
    mode: 'cors',
    signal,
  });

  if (!response.ok) {
    return null;
  }

  const imageBlob = await response.blob();
  if (!imageBlob.type.startsWith('image/')) {
    return null;
  }

  const extension =
    imageBlob.type === 'image/jpeg'
      ? 'jpg'
      : imageBlob.type.split('/')[1] || 'png';

  return new File([imageBlob], `rewards-offer.${extension}`, {
    type: imageBlob.type,
  });
}

export function ThankYouShare({
  url,
  name,
  isOffer,
  rewardsOfferImg,
  contactUid,
}: ThankYouShareProps): JSX.Element {
  const { width } = useViewportSize();
  const { company } = useContext(CompanyContext);
  const [shareImageFile, setShareImageFile] = useState<File | null>(null);
  const [isShareImageLoading, setIsShareImageLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [isTouchDevice] = useState(() => detectTouchDevice());
  const [isIos] = useState(() => detectIos());

  useEffect(() => {
    preloadFacebookSdk();
  }, []);
  const { offerImg, isGenerating: isGeneratingOfferImg } = useOfferImage(
    contactUid,
    rewardsOfferImg,
  );
  const isPreparingShareImage = isGeneratingOfferImg || isShareImageLoading;
  const canShare = typeof navigator.share === 'function';
  const canShareFiles = Boolean(
    shareImageFile &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [shareImageFile] }),
  );
  const options = shareOptions({
    canShare,
    canShareFiles,
    isTouchDevice,
    hasImageFile: Boolean(shareImageFile),
    isPreparingImage: isPreparingShareImage,
    isIos,
  });

  const companyName = company?.name ?? 'this company';
  const referralRewardsEnabled =
    isOffer && Boolean(company?.referralRewardsEnabled);
  const referralOfferEnabled =
    referralRewardsEnabled && Boolean(company?.referralOfferEnabled);
  const showReward = referralRewardsEnabled && Boolean(company?.promoterReward);

  // The promoter's own image exists only after a friend claims; until then the client's offer image is shared.
  const hasShareImage = Boolean(offerImg || company?.offerImgUrl);

  useEffect(() => {
    setShareImageFile(null);

    if (!hasShareImage || !company?.uniqueid) {
      setIsShareImageLoading(false);
      return;
    }

    const abortController = new AbortController();
    setIsShareImageLoading(true);

    // The Space blocks cross-origin reads, so the bytes come through the platform's proxy.
    const preloadShareImage = async () => {
      try {
        const imageFile = await createShareImageFile(
          offerImageProxyUrl(
            import.meta.env.VITE_BASE_URL,
            company.uniqueid,
            contactUid,
          ),
          abortController.signal,
        );
        setShareImageFile(imageFile);
      } catch {
        // The URL-only share remains available if the CDN image cannot load.
      } finally {
        if (!abortController.signal.aborted) {
          setIsShareImageLoading(false);
        }
      }
    };

    void preloadShareImage();

    return () => abortController.abort();
    // offerImg flips from the client image to the promoter's portrait once it is made; refetch then.
  }, [offerImg, hasShareImage, company?.uniqueid, contactUid]);

  const onClickEmail = () => {
    const subject = 'Check Out ' + (isOffer ? 'This Offer' : 'My Review');
    const offerBody = `Hi, ${name}. I wanted to let you know that ${company?.name} did an amazing job and they're offering ${company?.offerTitle}! Click here: \n${url}`;
    const reviewBody = `Hey,\nI recently left a review for ${company?.name}, you should check it out!\nHere is the link: ${url}`;
    safeOpen(
      encodeURI(
        `mailto:?subject=${subject}&body=${isOffer ? offerBody : reviewBody}`,
      ),
      'height=500,width=530,menubar=no,status=no,toolbar=no',
    );
  };

  const onClickMobile = () => {
    const offerBody = `Hi, ${name}. I wanted to let you know that ${company?.name} did an amazing job and they're offering ${company?.offerTitle}! Click here: \n${url}`;
    const reviewBody = `Hey,\nI recently left a review for ${company?.name}, you should check it out!\nHere is the link: ${url}`;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    // iOS uses &, while Android expects ? before the message body.
    // Leave the recipient empty so the messaging app opens its recipient picker.
    const separator = isIOS ? '&' : '?';
    const body = encodeURIComponent(isOffer ? offerBody : reviewBody);
    window.location.assign(`sms:${separator}body=${body}`);
  };

  const onClickFacebook = () => {
    setShareStatus('');
    void shareToFacebook(url);
  };

  const onClickX = () => {
    setShareStatus('');
    safeOpen(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
      'height=500,width=530,menubar=no,status=no,toolbar=no',
    );
  };

  const saveImageFile = (): boolean => {
    if (!shareImageFile) return false;
    const objectUrl = URL.createObjectURL(shareImageFile);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = shareImageFile.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  };

  const onClickSaveImage = () => {
    setShareStatus('');
    if (!saveImageFile()) {
      setShareStatus(
        'The offer image is still being prepared. Try again in a moment.',
      );
      return;
    }
    setShareStatus(
      'Image saved. Post it anywhere, and paste your link with it.',
    );
  };

  // Desktop Instagram and TikTok: no share link exists, so save the image, copy the link, open the upload page.
  const onClickImageAppDesktop = async (app: 'Instagram' | 'TikTok') => {
    setShareStatus('');
    if (!saveImageFile()) {
      setShareStatus(
        'The offer image is still being prepared. Try again in a moment.',
      );
      return;
    }
    let linkCopied = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        linkCopied = true;
      } catch {
        // The link is shown in the status line instead.
      }
    }
    const uploadPage =
      app === 'TikTok'
        ? 'https://www.tiktok.com/upload'
        : 'https://www.instagram.com/';
    window.open(uploadPage, '_blank', 'noopener');
    setShareStatus(
      linkCopied
        ? `Image saved and link copied. Upload the image on ${app} and paste the link in your caption.`
        : `Image saved. Upload it on ${app} and add this link to your caption: ${url}`,
    );
  };

  const copyUrlToClipboard = async () => {
    let didCopy = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        didCopy = true;
      } catch {
        // Fall back for browsers that expose Clipboard but deny access to it.
      }
    }

    if (!didCopy) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';

      try {
        document.body.appendChild(textArea);
        textArea.select();
        didCopy = document.execCommand('copy');
      } catch {
        // The URL is displayed below the buttons if clipboard access fails.
      } finally {
        textArea.remove();
      }
    }

    setShareStatus(
      didCopy ? 'Link copied to clipboard!' : `Copy this link: ${url}`,
    );
  };

  const onClickSocialMedia = async () => {
    setShareStatus('');

    if (typeof navigator.share !== 'function') {
      await copyUrlToClipboard();
      return;
    }

    const shareMessage = isOffer
      ? `Click the link to claim this special offer${company?.name ? ` from ${company.name}` : ''}:`
      : `Check out my review of ${company?.name ?? 'SoTellUs'}:`;
    const shareLink = async () => {
      try {
        await navigator.share({
          text: shareMessage,
          url,
        });
        return true;
      } catch (error) {
        return error instanceof DOMException && error.name === 'AbortError';
      }
    };

    if (shareImageFile) {
      if (
        typeof navigator.canShare !== 'function' ||
        !navigator.canShare({ files: [shareImageFile] })
      ) {
        if (!(await shareLink())) {
          await copyUrlToClipboard();
        }
        return;
      }

      // Share the image as the primary item and provide the referral URL as
      // accompanying text so compatible targets receive both.
      try {
        await navigator.share({
          files: [shareImageFile],
          text: `${shareMessage} ${url}`,
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          if (!(await shareLink())) {
            await copyUrlToClipboard();
          }
        }
      }
      return;
    }

    if (!(await shareLink())) {
      await copyUrlToClipboard();
    }
  };

  return (
    <div className="centered-stack">
      {referralOfferEnabled && company?.offerImgUrl && (
        <div className="logo-wrapper offer-image-wrapper">
          <img
            className="logo-img offer-image"
            alt="The offer your friends receive"
            src={company?.offerImgUrl}
          />
        </div>
      )}
      <Title
        className="header-text share-title"
        mb={10}
        mt={10}
        style={{ textWrap: 'balance' }}
      >
        {isOffer
          ? referralRewardsEnabled && !referralOfferEnabled
            ? `Refer ${companyName} to your family & friends`
            : `Share this offer from ${companyName} with your family & friends`
          : `Share your review of ${companyName} with your family & friends`}
      </Title>
      {/* Two different numbers: the friend's offer is on the image, the promoter's reward is theirs. */}
      {showReward && (
        <p className="reward-line">
          {referralOfferEnabled && company?.offerTitle
            ? `Your friends get "${company.offerTitle}". `
            : ''}
          You earn{' '}
          <span className="reward-amount">${company?.promoterReward}</span>
          {' '}for every friend who claims it.
        </p>
      )}
      {/* Facebook: its own share dialog, which previews the link. */}
      {options.facebook && (
        <button
          className="platform-button"
          type="button"
          onClick={onClickFacebook}
        >
          <div className="social-icon">{facebookIconPrimary('30', '30')}</div>
          <p className="button-label">Share to Facebook</p>
        </button>
      )}

      {options.x && (
        <button className="platform-button" type="button" onClick={onClickX}>
          <div className="social-icon">{xIcon('30', '35')}</div>
          <p className="button-label">Share to X</p>
        </button>
      )}

      {/* Instagram and TikTok have no share link: phones hand them the image through the system sheet,
          desktops save the image, copy the link and open the upload page. */}
      {options.imageApps && (
        <button
          className="platform-button"
          type="button"
          onClick={() =>
            void (options.imageAppsMode === 'sheet'
              ? onClickSocialMedia()
              : onClickImageAppDesktop('Instagram'))
          }
          disabled={options.imageAppsPending}
        >
          <div className="social-icon">
            <IconBrandInstagram color="#E4405F" size={35} />
          </div>
          <p className="button-label">
            {options.imageAppsPending
              ? 'Preparing your offer image...'
              : 'Share to Instagram'}
          </p>
        </button>
      )}

      {/* Android phones reach TikTok through the sheet; desktops save the image and open the upload page. */}
      {options.tiktok && (
        <button
          className="platform-button"
          type="button"
          onClick={() =>
            void (options.imageAppsMode === 'sheet'
              ? onClickSocialMedia()
              : onClickImageAppDesktop('TikTok'))
          }
          disabled={options.imageAppsPending}
        >
          <div className="social-icon">{tiktokIcon('30', '30')}</div>
          <p className="button-label">
            {options.imageAppsPending
              ? 'Preparing your offer image...'
              : 'Share to TikTok'}
          </p>
        </button>
      )}

      {options.moreWays && (
        <button
          className="platform-button"
          type="button"
          onClick={() => void onClickSocialMedia()}
        >
          <div className="social-icon">
            <IconShare color="gray" size={35} />
          </div>
          <p className="button-label">More ways to share</p>
        </button>
      )}

      {options.saveImage && (
        <button
          className="platform-button"
          type="button"
          onClick={onClickSaveImage}
          disabled={!shareImageFile}
        >
          <div className="social-icon">
            <IconDeviceFloppy color="gray" size={35} />
          </div>
          <p className="button-label">
            {shareImageFile
              ? 'Save the offer image'
              : 'Preparing your offer image...'}
          </p>
        </button>
      )}

      {options.copyLink && (
        <button
          className="platform-button"
          type="button"
          onClick={() => void copyUrlToClipboard()}
        >
          <div className="social-icon">
            <IconCopy color="gray" size={35} />
          </div>
          <p className="button-label">Copy the link</p>
        </button>
      )}
      {shareStatus && <p role="status">{shareStatus}</p>}
      {/* Email Option */}
      <button className="platform-button" type="button" onClick={onClickEmail}>
        <div className="social-icon">
          <IconMail color="gray" size={35} />
        </div>
        <p className="button-label">Share through Email</p>
      </button>
      {/* Mobile Option */}
      {width < 750 && (
        <button
          className="platform-button"
          type="button"
          onClick={onClickMobile}
        >
          <div className="social-icon">
            <IconMessage2 color="gray" size={35} />
          </div>
          <p className="button-label">Share through SMS</p>
        </button>
      )}
    </div>
  );
}
